import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { 
  StickyNote, Bookmark, Plus, Pencil, Trash2, BookOpen, Video
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_COLORS = [
  { id: "default", label: "Standard", class: "bg-card" },
  { id: "yellow", label: "Gelb", class: "bg-yellow-500/10 border-yellow-500/30" },
  { id: "green", label: "Grün", class: "bg-green-500/10 border-green-500/30" },
  { id: "blue", label: "Blau", class: "bg-blue-500/10 border-blue-500/30" },
  { id: "red", label: "Rot", class: "bg-red-500/10 border-red-500/30" }
];

const Lernraum = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("notizen");
  const [notes, setNotes] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [noteDialog, setNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  
  // Form
  const [noteForm, setNoteForm] = useState({ title: "", content: "", color: "default" });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "notizen") {
        const res = await axios.get(`${API}/lernraum/notes`);
        setNotes(res.data);
      } else {
        const res = await axios.get(`${API}/lernraum/saved`);
        setSaved(res.data);
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteForm.title) {
      toast.error("Bitte Titel eingeben");
      return;
    }
    try {
      if (editingNote) {
        await axios.put(`${API}/lernraum/notes/${editingNote.id}`, null, {
          params: { title: noteForm.title, content: noteForm.content, color: noteForm.color }
        });
        toast.success("Notiz aktualisiert");
      } else {
        await axios.post(`${API}/lernraum/notes`, null, {
          params: { title: noteForm.title, content: noteForm.content, color: noteForm.color }
        });
        toast.success("Notiz erstellt");
      }
      setNoteDialog(false);
      setEditingNote(null);
      setNoteForm({ title: "", content: "", color: "default" });
      loadData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Notiz löschen?")) return;
    try {
      await axios.delete(`${API}/lernraum/notes/${noteId}`);
      toast.success("Notiz gelöscht");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleUnsave = async (savedId) => {
    try {
      await axios.delete(`${API}/lernraum/saved/${savedId}`);
      toast.success("Entfernt");
      loadData();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({ title: note.title, content: note.content, color: note.color || "default" });
    setNoteDialog(true);
  };

  const openNewNote = () => {
    setEditingNote(null);
    setNoteForm({ title: "", content: "", color: "default" });
    setNoteDialog(true);
  };

  const getNoteColorClass = (color) => {
    return NOTE_COLORS.find(c => c.id === color)?.class || "bg-card";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric"
    });
  };

  return (
    <ParticipantLayout title="Mein Lernraum">
      <div className="space-y-6" data-testid="lernraum-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <p className="text-muted-foreground">
              Ihr persönlicher Bereich für Notizen und gespeicherte Inhalte.
            </p>
          </div>
          {activeTab === "notizen" && (
            <Button onClick={openNewNote}>
              <Plus className="h-4 w-4 mr-2" />
              Neue Notiz
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="notizen" className="gap-2">
              <StickyNote className="h-4 w-4" />
              Notizen ({notes.length})
            </TabsTrigger>
            <TabsTrigger value="gespeichert" className="gap-2">
              <Bookmark className="h-4 w-4" />
              Gespeichert ({saved.length})
            </TabsTrigger>
          </TabsList>

          {/* Notes View */}
          <TabsContent value="notizen" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notes.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <StickyNote className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Notizen</p>
                  <p className="text-sm mt-1">Erstellen Sie Ihre erste Notiz zum Lernen.</p>
                  <Button className="mt-4" onClick={openNewNote}>
                    <Plus className="h-4 w-4 mr-2" />
                    Erste Notiz erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {notes.map(note => (
                  <Card key={note.id} className={`${getNoteColorClass(note.color)} border-border hover:shadow-lg transition-shadow`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">{note.title}</CardTitle>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditNote(note)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteNote(note.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">
                        {note.content || "Keine Inhalte"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-3">
                        Aktualisiert: {formatDate(note.updated_at)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Saved Content View */}
          <TabsContent value="gespeichert" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : saved.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Bookmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Keine gespeicherten Inhalte</p>
                  <p className="text-sm mt-1">Speichern Sie Kurse und Medien für später.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {saved.map(item => (
                  <Card key={item.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            {item.content_type === "course" ? (
                              <BookOpen className="h-5 w-5 text-primary" />
                            ) : (
                              <Video className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium">{item.details?.title || "Inhalt"}</h4>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Badge variant="outline" className="text-xs">
                                {item.content_type === "course" ? "Kurs" : "Medien"}
                              </Badge>
                              {item.details?.category && (
                                <span>{item.details.category}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleUnsave(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Note Dialog */}
        <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{editingNote ? "Notiz bearbeiten" : "Neue Notiz"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Farbe</Label>
                <Select value={noteForm.color} onValueChange={(v) => setNoteForm({ ...noteForm, color: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTE_COLORS.map(color => (
                      <SelectItem key={color.id} value={color.id}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${color.class} border`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={noteForm.title}
                  onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                  placeholder="z.B. Wichtige Formeln"
                />
              </div>
              <div className="space-y-2">
                <Label>Inhalt</Label>
                <Textarea
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  placeholder="Ihre Notizen..."
                  rows={8}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNoteDialog(false)}>Abbrechen</Button>
              <Button onClick={handleSaveNote}>
                {editingNote ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ParticipantLayout>
  );
};

export default Lernraum;
